import { UploadSimple, X } from "@phosphor-icons/react"
import { Tag, Toast } from "@vesatogo/grass-core"
import { useState } from "react"
import toast from "react-hot-toast"
import * as XLSX from "xlsx"
import FileUploadButton from "~/components/FIleUploadButton"

type ExcelFileUploadProps = {
  form: any
  fieldName: string
  displayName?: string
  isDisabled?: boolean
  className?: string
  userSourceKind?: "internal" | "external"
  helperText?: string
}

type ValidationResult = {
  isValid: boolean
  userCount: number
  errors: string[]
}

const DEFAULT_COST_PER_USER = 1
export const ExcelFileUpload = ({
  form,
  fieldName,
  displayName = "file_list",
  isDisabled = false,
  className = "",
  userSourceKind = "internal",
  helperText = "",
}: ExcelFileUploadProps) => {
  const fileValue = form?.values?.[fieldName]
  const [validationResult, setValidationResult] =
    useState<ValidationResult | null>(null)

  const validateExcelFile = (file: File): Promise<ValidationResult> => {
    return new Promise(resolve => {
      const reader = new FileReader()
      reader.onload = event => {
        const data = new Uint8Array(event.target?.result as ArrayBuffer)
        const workbook = XLSX.read(data, { type: "array" })
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
        const sheetData = XLSX.utils.sheet_to_json(firstSheet, {
          header: 1,
        }) as string[][]

        const errors: string[] = []

        if (sheetData.length === 0) {
          errors.push("Empty Excel file")
          resolve({ isValid: false, userCount: 0, errors })
          return
        }

        const headers = sheetData[0].map(h => h?.toString().trim())
        const expectedHeaders =
          userSourceKind === "internal"
            ? ["Sr. No.", "SF Code"]
            : ["Sr. No.", "First Name", "Last Name", "Number"]

        // Check for missing columns (case-insensitive)
        const missingHeaders = expectedHeaders.filter(
          expected =>
            !headers.some(h => h.toLowerCase() === expected.toLowerCase())
        )

        if (missingHeaders.length > 0) {
          errors.push(`Missing columns: ${missingHeaders.join(", ")}`)
        }

        // Get column indices
        const getColumnIndex = (columnName: string) =>
          headers.findIndex(h => h.toLowerCase() === columnName.toLowerCase())

        const sfCodeIndex = getColumnIndex("SF Code")
        const firstNameIndex = getColumnIndex("First Name")
        const lastNameIndex = getColumnIndex("Last Name")
        const numberIndex = getColumnIndex("Number")

        // Filter out empty rows and validate row data
        const validRows: any[] = []
        const rowErrors: string[] = []

        sheetData.slice(1).forEach((row, index) => {
          const excelRowNumber = index + 2 // +2 because Excel is 1-indexed and we skip header

          // Check if row is completely empty
          const isEmptyRow =
            !row ||
            row.length === 0 ||
            !row.some(
              cell =>
                cell !== null &&
                cell !== undefined &&
                cell.toString().trim() !== ""
            )

          if (isEmptyRow) {
            return // Skip empty rows without counting them
          }

          // Validate based on source kind
          if (userSourceKind === "internal") {
            // Check SF Code is filled
            const sfCode =
              sfCodeIndex >= 0 ? row[sfCodeIndex]?.toString().trim() : ""
            if (!sfCode) {
              rowErrors.push(
                `Row ${excelRowNumber}: SF Code is missing or empty`
              )
            } else {
              validRows.push(row)
            }
          } else {
            // External source - check First Name, Last Name, Number
            const firstName =
              firstNameIndex >= 0 ? row[firstNameIndex]?.toString().trim() : ""
            const lastName =
              lastNameIndex >= 0 ? row[lastNameIndex]?.toString().trim() : ""
            const number =
              numberIndex >= 0 ? row[numberIndex]?.toString().trim() : ""

            const missingFields: string[] = []
            if (!firstName) missingFields.push("First Name")
            if (!lastName) missingFields.push("Last Name")
            if (!number) missingFields.push("Number")

            if (missingFields.length > 0) {
              rowErrors.push(
                `Row ${excelRowNumber}: Missing ${missingFields.join(", ")}`
              )
            } else {
              validRows.push(row)
            }
          }
        })

        // Add row-level errors to the errors array
        if (rowErrors.length > 0) {
          errors.push(...rowErrors)
        }

        const userCount = validRows.length

        // Check if file has no valid data rows
        if (userCount === 0 && errors.length === 0) {
          errors.push(
            "Excel file contains no data rows (only headers or empty rows)"
          )
        }

        resolve({
          isValid: errors.length === 0,
          userCount,
          errors,
        })
      }
      reader.onerror = () => {
        resolve({
          isValid: false,
          userCount: 0,
          errors: ["Failed to read file"],
        })
      }
      reader.readAsArrayBuffer(file)
    })
  }

  const handleFileSelect = async (files: FileList | null): Promise<boolean> => {
    if (!files || files.length === 0) {
      setValidationResult(null)
      return true // Allow clearing selection
    }

    const file = files[0]
    const result = await validateExcelFile(file)
    setValidationResult(result)

    if (result?.errors?.length > 0) {
      // Show error toast
      toast.custom(
        <Toast
          intent="danger"
          title="Invalid Excel file format"
          subtitle={result.errors.join(", ")}
        />,
        { duration: 5000 }
      )
      return false // Block upload
    }
    return true // Allow upload
  }

  return (
    <div className={`flex flex-col gap-2 mt-2 ${className}`}>
      <div className="flex flex-row items-center gap-2 bg-gray-100 p-1 pl-2">
        <span className="text-green-500 text-sm ">
          • Upload only excel file :
        </span>
        {!fileValue ? (
          <FileUploadButton
            buttonLabel="Upload"
            buttonIcon={<UploadSimple />}
            accept=".xlsx, .xls"
            onError={msg => {
              // File upload errors are handled by validation
              console.error(msg)
            }}
            onSuccess={message => {
              const media = message.map(item => ({
                media: item.data.media,
              }))?.[0]
              form.setFieldValue(fieldName, media)
            }}
            onFileSelect={handleFileSelect}
            disabled={isDisabled}
            className="!w-fit"
            method="put"
            appName="setutrade"
            environment={import.meta.env.VITE_APP_ENV}
            endpoint={`https://${
              import.meta.env.VITE_REST_URL
            }/rest/trade/uploader/`}
            maxFiles={1}
            buttonProps={{
              size: "sm",
              variant: "minimal",
              disabled: isDisabled,
            }}
          />
        ) : (
          <Tag
            intent="none"
            className="!bg-white !text-gray-600 border border-gray-400 !rounded-xl"
          >
            <div className="flex gap-1 items-center">
              {displayName}{" "}
              <X
                onClick={() => {
                  if (isDisabled) return
                  form.setFieldValue(fieldName, null)
                  setValidationResult(null)
                }}
              />
            </div>
          </Tag>
        )}
        {helperText && <div className="text-sm text-red-600">{helperText}</div>}
      </div>

      {validationResult?.errors?.length &&
      validationResult?.errors?.length > 0 ? (
        <>
          {validationResult.errors.map((error, idx) => (
            <div key={idx} className="flex items-center gap-1">
              <Toast
                title={error}
                intent="danger"
                variant="minimal"
                className="max-w-full text-xs "
                titleClassName="mt-1"
              />
            </div>
          ))}
        </>
      ) : validationResult?.userCount ? (
        <Toast
          className="max-w-full text-xs"
          intent="info"
          variant="minimal"
          title={`“The approximate number of users is ${
            validationResult?.userCount
          } for sending WhatsApp messages. At ₹${DEFAULT_COST_PER_USER} per user, the tentative total cost will be ₹${
            (validationResult?.userCount || 0) * DEFAULT_COST_PER_USER
          }.”`}
          titleClassName="!text-blue-300"
        />
      ) : null}
    </div>
  )
}
